import { DispatchStep, IntegrationStepConfig, TriggerStep } from '@/types/automationSteps.ts';
import { Accordion, Alert, Box, Divider, Stack, Text } from '@mantine/core';
import CustomLoadingOverlay from '@/components/CustomLoadingOverlay.tsx';
import IntegrationField from '@/pages/Editor/components/SharedFields/IntegrationFields/IntegrationField.tsx';
import IntegrationFieldSensitive from '@/pages/Editor/components/SharedFields/IntegrationFields/IntegrationFieldSensitive.tsx';
import React, { useCallback, useMemo, useState } from 'react';
import useStepSpecificError from '@/hooks/useStepSpecificError.ts';
import { StepErrorType } from '@/types/stepErrorTypes.ts';
import { IntegrationActionConfig, IntegrationConfigField } from '@/types/integrations.ts';
import { withBrowserModelSelection } from './browserModelSelection.ts';

type props = {
  step: TriggerStep | DispatchStep;
  stepConfig: IntegrationStepConfig;
  actionConfig: IntegrationActionConfig;
  onChangeIntegrationConfigNoDelay: (updated_config: IntegrationStepConfig) => void;
  onChangeIntegrationConfigDelay: (updated_config: IntegrationStepConfig) => void;
  showAccordion?: boolean;
  allowSensitiveFields?: boolean;
};

const isSensitiveField = (field: IntegrationConfigField): boolean => {
  return field.is_sensitive && field.type !== 'webhook';
};

const IntegrationConfigFields = ({
  step,
  stepConfig,
  actionConfig,
  onChangeIntegrationConfigNoDelay,
  onChangeIntegrationConfigDelay,
  showAccordion = true,
  allowSensitiveFields = true,
}: props) => {
  const effectiveStepConfig = useMemo(
    () => withBrowserModelSelection(stepConfig, actionConfig),
    [stepConfig, actionConfig],
  );
  const onFieldChangeNoDelay = useCallback(
    (fieldId: string, updated_value: any) => {
      const updatedFields = effectiveStepConfig.config_values.fields.map((field_value) =>
        field_value.field_id === fieldId ? { ...field_value, value: updated_value } : field_value,
      );
      onChangeIntegrationConfigNoDelay({
        ...effectiveStepConfig,
        config_values: { fields: updatedFields },
      });
    },
    [effectiveStepConfig, onChangeIntegrationConfigNoDelay],
  );

  const onFieldChangeDelay = useCallback(
    (fieldId: string, updated_value: any) => {
      const updatedFields = effectiveStepConfig.config_values.fields.map((field_value) =>
        field_value.field_id === fieldId ? { ...field_value, value: updated_value } : field_value,
      );
      onChangeIntegrationConfigDelay({
        ...effectiveStepConfig,
        config_values: { fields: updatedFields },
      });
    },
    [effectiveStepConfig, onChangeIntegrationConfigDelay],
  );

  // IMPORTANT: Get the field value from the useUpdateStepFields hook to get the correct (UI) state
  // Get the field config (errors, is_hidden, is_disabled) from the step.config_values.fields -> since it's the source of truth for the step configuration
  // Browser model visibility follows the current selection, including defaults absent from historical versions.
  const getFieldValue = useCallback(
    (field: IntegrationConfigField) =>
      effectiveStepConfig.config_values.fields.find((field_value) => field_value.field_id === field.id)
        ?.value ?? null,
    [effectiveStepConfig.config_values],
  );

  const getFieldValueConfig = useCallback(
    (field: IntegrationConfigField) => {
      const canonical = step.integration_config.config_values.fields.find(
        (field_value) => field_value.field_id === field.id,
      );
      if (effectiveStepConfig !== stepConfig && (field.id === 'browser_model' || field.id === 'browser_model_mode')) {
        const projected = effectiveStepConfig.config_values.fields.find(
          (fieldValue) => fieldValue.field_id === field.id,
        );
        if (projected) {
          return { ...(canonical ?? projected), is_hidden: projected.is_hidden };
        }
      }
      return canonical || {
        field_id: field.id,
        value: null,
        is_hidden: false,
        is_disabled: false,
        errors: [],
      };
    },
    [effectiveStepConfig, stepConfig, step.integration_config.config_values.fields],
  );

  const [showActionConfig, setShowActionConfig] = useState<string | null>(null);

  const fieldsDisabled = step.integration_config.action_type != stepConfig.action_type;

  const actionConfigError = useStepSpecificError(step, StepErrorType.InvalidIntegrationConfigValue);

  const actionConfigElement = (
    <>
      {!allowSensitiveFields && actionConfig.fields.some(isSensitiveField) && (
        <Alert color="gray">
          Sensitive integration values are protected for module steps.
        </Alert>
      )}
      {actionConfig.fields.map((field, idx) => {
        const fieldValue = getFieldValue(field);
        const fieldValueConfig = getFieldValueConfig(field);
        if (fieldValueConfig.is_hidden) {
          return null;
        } else if (isSensitiveField(field)) {
          if (!allowSensitiveFields) {
            return null;
          }
          return (
            <IntegrationFieldSensitive
              key={field.id + idx.toString()}
              step={step}
              idx={idx}
              stepId={step.step_id}
              field={field}
              fieldValueConfig={fieldValueConfig}
              disabled={fieldsDisabled}
            />
          );
        } else {
          return (
            <IntegrationField
              key={field.id + idx.toString()}
              stepId={step.step_id}
              field={field}
              fieldValue={fieldValue}
              fieldValueConfig={fieldValueConfig}
              onChangeDelay={onFieldChangeDelay}
              onChangeNoDelay={onFieldChangeNoDelay}
              disabled={fieldsDisabled}
            />
          );
        }
      })}
    </>
  );

  if (!showAccordion) {
    return actionConfigElement;
  }

  return (
    <>
      <Divider />
      <Box pos="relative">
        <CustomLoadingOverlay visible={fieldsDisabled} />
        <Accordion
          value={showActionConfig}
          onChange={(value) => setShowActionConfig(value)}
          variant={'separated'}
        >
          <Accordion.Item
            value={'action-config'}
            style={{
              borderColor: actionConfigError ? '#f34f52' : '#ececed',
              background: actionConfigError && !showActionConfig ? '#fdeded' : 'transparent',
            }}
          >
            <Accordion.Control>
              <Text size="sm" fw={500}>
                Action Configuration
              </Text>
            </Accordion.Control>
            <Accordion.Panel styles={{ content: { padding: 0 } }}>
              {showActionConfig && (
                <Stack p={'xs'} pt={0}>
                  {actionConfigElement}
                </Stack>
              )}
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Box>
    </>
  );
};

export default IntegrationConfigFields;
